import { XMarkIcon } from "@heroicons/react/24/outline";
import { FeedbackType, ReferenceContactId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import React, { useCallback, useEffect, useState } from "react";
import { useFeature } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import {
    clearOcrFieldsForFeedback,
    peekOcrFieldsForFeedback,
} from "./business-card-scan-feedback-store";
import { InlineFeedback, InlineFeedbackResponse } from "./inline-feedback";

const NEGATIVE_FEEDBACK_OPTIONS = [
    "Wrong name",
    "Wrong title",
    "Wrong organization",
    "Wrong email",
    "Wrong phone",
    "Wrong address",
    "Missing fields",
    "Other (see notes)",
];

interface BusinessCardScanFeedbackPromptProps {
    referenceContactId: ReferenceContactId;
}

export const BusinessCardScanFeedbackPrompt: React.FC<
    BusinessCardScanFeedbackPromptProps
> = ({ referenceContactId }) => {
    const enabled = useFeature("businessCardScan");
    const trpcClient = trpc.useTRPC();

    const [ocrFields, setOcrFields] = useState(() =>
        peekOcrFieldsForFeedback(referenceContactId),
    );
    const [submitted, setSubmitted] = useState<InlineFeedbackResponse>(null);
    const [dismissed, setDismissed] = useState(false);
    const [thanks, setThanks] = useState(false);

    useEffect(() => {
        setOcrFields(peekOcrFieldsForFeedback(referenceContactId));
        setSubmitted(null);
        setDismissed(false);
        setThanks(false);
    }, [referenceContactId]);

    const submitMutation = useMutation({
        ...trpcClient.businessCardScanFeedback.submitFeedback.mutationOptions(),
        onSuccess: () => {
            clearOcrFieldsForFeedback(referenceContactId);
            setThanks(true);
        },
    });

    const handlePositive = useCallback(() => {
        if (!ocrFields) return;
        setSubmitted("positive");
        handleEvent("scan-business-card-feedback-submit", { sentiment: "positive" });
        submitMutation.mutate({
            referenceContactId,
            feedbackType: FeedbackType.Positive,
            feedbackText: null,
            ocrFields,
        });
    }, [ocrFields, referenceContactId, submitMutation]);

    const handleNegativeIntent = useCallback(() => {
        setSubmitted("negative");
        handleEvent("scan-business-card-feedback-intent", { sentiment: "negative" });
    }, []);

    const handleNegativeSubmit = useCallback(
        (combinedText: string | null) => {
            if (!ocrFields) return;
            handleEvent("scan-business-card-feedback-submit", { sentiment: "negative" });
            submitMutation.mutate({
                referenceContactId,
                feedbackType: FeedbackType.Negative,
                feedbackText: combinedText,
                ocrFields,
            });
        },
        [ocrFields, referenceContactId, submitMutation],
    );

    const handleDismiss = useCallback(() => {
        clearOcrFieldsForFeedback(referenceContactId);
        setDismissed(true);
    }, [referenceContactId]);

    if (!enabled || !ocrFields || dismissed) {
        return null;
    }

    if (thanks) {
        return (
            <div className="mx-0 md:mx-2 lg:mx-4 my-3 px-4 py-3 rounded-md bg-green-50 text-sm text-green-800 flex items-center justify-between">
                <span>Thanks — your feedback was recorded.</span>
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label="Dismiss feedback prompt"
                    className="text-green-700 hover:text-green-900 p-1 rounded">
                    <XMarkIcon className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return (
        <div className="mx-0 md:mx-2 lg:mx-4 my-3 px-4 py-3 rounded-md bg-green-50">
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-2 flex-1">
                    <p className="text-sm font-medium text-green-800">
                        How did the business card scan go?
                    </p>
                    <p className="text-xs text-green-700">
                        Let us know if the contact info was captured correctly. Your
                        feedback helps us improve the scanner.
                    </p>
                    <InlineFeedback
                        negativeFeedbackPrompt="What didn't work?"
                        negativeFeedbackOptions={NEGATIVE_FEEDBACK_OPTIONS}
                        submitted={submitted}
                        submitting={submitMutation.isPending}
                        positiveAriaLabel="The scan was accurate"
                        negativeAriaLabel="The scan had problems"
                        onPositive={handlePositive}
                        onNegativeIntent={handleNegativeIntent}
                        onNegativeSubmit={handleNegativeSubmit}
                    />
                </div>
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label="Dismiss feedback prompt"
                    className="text-green-700 hover:text-green-900 p-1 rounded">
                    <XMarkIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
